import { inject, Injectable } from '@angular/core';
import { HttpGatewayService } from '../../global/services/http-gateway.service';
import { Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import {
  PlaceAutocompleteOption,
  CenterPoint,
  CreateLoopContract,
  CustomWaypointContract,
  LoopContract,
  UpdateLoopMapSettingsContract,
  VisitContract,
} from '../store/loops/types';
import { AccountContract } from '../store/accounts/types';

@Injectable({ providedIn: 'root' })
export class LoopsApi {
  private httpService = inject(HttpGatewayService);

  public getLoops(
    startDate: Date,
    endDate: Date,
    salesRepId?: string,
  ): Observable<LoopContract[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('startDate', startDate.toISOString());
    queryParams = queryParams.set('endDate', endDate.toISOString());
    if (salesRepId) {
      queryParams = queryParams.set('salesRepId', salesRepId);
    }
    return this.httpService.get<LoopContract[]>('/loops', queryParams);
  }

  public getLoopByDate(
    date: Date,
    salesRepId?: string,
  ): Observable<LoopContract> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('date', date.toISOString());
    if (salesRepId) {
      queryParams = queryParams.set('salesRepId', salesRepId);
    }
    return this.httpService.get<LoopContract>('/loops/date', queryParams);
  }

  public clearLoopById(loopId: string): Observable<LoopContract> {
    return this.httpService.put<LoopContract>(
      `/loops/${loopId}/clear`,
      undefined,
    );
  }

  public getVisitSuggestion(
    date: Date,
    rangeInM: number,
    loopId?: string,
  ): Observable<VisitContract> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('date', date.toISOString());
    queryParams = queryParams.set('rangeInM', rangeInM);
    return this.httpService.get<VisitContract>(
      loopId ? `/loops/suggestion/${loopId}` : '/loops/suggestion',
      queryParams,
    );
  }

  public createLoop(
    createLoopContract: CreateLoopContract,
  ): Observable<LoopContract> {
    // Set loop date to noon
    createLoopContract.date = new Date(
      createLoopContract.date.setHours(12, 0, 0, 0),
    );
    return this.httpService.post<LoopContract>('/loops', createLoopContract);
  }

  public addVisitToLoop(
    loopId: string,
    visit: VisitContract,
  ): Observable<LoopContract> {
    return this.httpService.post<LoopContract>(`/loops/${loopId}/visit`, visit);
  }

  public deleteVisitFromLoop(
    loopId: string,
    visitId: string,
  ): Observable<LoopContract> {
    return this.httpService.delete<LoopContract>(
      `/loops/${loopId}/visit/${visitId}`,
    );
  }

  public addTimeToVisit(
    time: Date,
    loopId: string,
    visitId: string,
  ): Observable<LoopContract> {
    return this.httpService.put<LoopContract>(
      `/loops/${loopId}/visit/${visitId}`,
      { time: time.toISOString() },
    );
  }

  public getNearbyAccounts(
    loopId: string,
    rangeInM: number,
  ): Observable<AccountContract[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('rangeInM', rangeInM.toString());
    return this.httpService.get<AccountContract[]>(
      `/loops/${loopId}/nearby`,
      queryParams,
    );
  }

  public confirmLoop(loop: LoopContract): Observable<LoopContract> {
    return this.httpService.put<LoopContract>(
      `/loops/${loop.id}/confirm`,
      loop,
    );
  }

  public createWarmVisit(
    accountId: string,
    date: Date,
    centerPoint?: CenterPoint,
    rangeInM?: number,
    startTime?: string,
    endTime?: string,
    averageVisitDurationInMinutes?: number,
  ): Observable<LoopContract> {
    return this.httpService.post<LoopContract>('/loops/warm-visit', {
      accountId,
      date: date.toISOString(),
      centerPoint,
      rangeInM,
      startTime,
      endTime,
      averageVisitDurationInMinutes,
    });
  }

  public getCustomWaypointAutocompleteOptions(
    input: string,
    sessionToken: string,
    loopId?: string,
  ) {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('input', input);
    queryParams = queryParams.set('sessiontoken', sessionToken);
    if (loopId) {
      queryParams = queryParams.set('loopId', loopId);
    }
    return this.httpService.get<PlaceAutocompleteOption[]>(
      `/loops/custom-waypoint/autocomplete`,
      queryParams,
    );
  }

  public addHomeVisitToLoop(
    loopId: string,
    placeId: string,
    name: string,
  ): Observable<LoopContract> {
    return this.httpService.post<LoopContract>(`/loops/${loopId}/home-visit`, {
      placeId,
      name,
    });
  }

  public addCustomWaypointToLoop(
    loopId: string,
    placeId: string,
    name: string,
    sessionToken: string,
  ): Observable<LoopContract> {
    return this.httpService.post<LoopContract>(
      `/loops/${loopId}/custom-waypoint`,
      { placeId, name, sessionToken },
    );
  }

  public deleteCustomWaypointFromLoop(
    loopId: string,
    customWaypointId: string,
  ): Observable<LoopContract> {
    return this.httpService.delete<LoopContract>(
      `/loops/${loopId}/custom-waypoint/${customWaypointId}`,
    );
  }

  public snoozeVisit(visitId: string, date: Date): Observable<LoopContract> {
    return this.httpService.post<LoopContract>(`/loops/snooze/${visitId}`, {
      date: date.toISOString(),
    });
  }

  public reorderLoop(
    loopId: string,
    visits: VisitContract[],
    customWaypoints: CustomWaypointContract[],
  ): Observable<LoopContract> {
    return this.httpService.put<LoopContract>(`/loops/${loopId}/reorder`, {
      visits,
      customWaypoints,
    });
  }

  public updateMapSettings(
    contract: UpdateLoopMapSettingsContract,
    loopId: string,
  ): Observable<LoopContract> {
    return this.httpService.put<LoopContract>(
      `/loops/${loopId}/map-settings`,
      contract,
    );
  }

  public getPlaceLatLng(
    placeId: string,
  ): Observable<{ latitude: number; longitude: number }> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('placeId', placeId);
    return this.httpService.get<{ latitude: number; longitude: number }>(
      `/loops/place-lat-lng`,
      queryParams,
    );
  }

  public updateStartHome(
    loopId: string,
    startHome: boolean,
  ): Observable<LoopContract> {
    return this.httpService.put<LoopContract>(`/loops/${loopId}/start-home`, {
      startHome,
    });
  }

  public updateEndHome(
    loopId: string,
    endHome: boolean,
  ): Observable<LoopContract> {
    return this.httpService.put<LoopContract>(`/loops/${loopId}/end-home`, {
      endHome,
    });
  }

  public optimizeRoute(loopId: string): Observable<LoopContract> {
    return this.httpService.put<LoopContract>(
      `/loops/${loopId}/optimize-route`,
      undefined,
    );
  }
}
